import { closestCenter, DndContext, DragOverlay, KeyboardSensor, MouseSensor, useDroppable, useSensor, useSensors, type DragEndEvent, type DragOverEvent, type Modifier } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { CollectionGroup, CollectionTreeNode } from '@tomo/core';
import { ContextMenu, IconButton, useConfirm, type MenuItem, type PaletteColor } from '@tomo/ui';
import { ArrowDown, ArrowUp, ChevronDown, Folder, FolderOpen, Pencil, Plus, Shapes, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { NavLink } from 'react-router';
import { useProject } from '../../data/hooks.tsx';
import { Icon } from '../../lib/icons.tsx';
import {
  applyDrop,
  buildRows,
  resolveDrop,
  startRenaming,
  stopRenaming,
  toggleGroup,
  useCollapsedGroups,
  useRenamingRow,
  type Row,
} from './collection-groups.ts';
import { IconPicker } from './IconPicker.tsx';
import { openNewCollection } from './ui-state.ts';

// The lifted copy only moves up and down, like the list it came from.
const verticalOnly: Modifier = ({ transform }) => ({ ...transform, x: 0 });

/**
 * The collection list: collections and the groups they are filed under, in the user's order. Drag
 * with the mouse (or Space, then the arrows), or use the context menu, which also has the moves a
 * drag can't express — like taking out the only collection in a group.
 */
export function CollectionList({
  tree,
  counts,
  base,
  readOnly,
  onNavigate,
}: {
  tree: CollectionTreeNode[];
  counts: Record<string, number>;
  base: string;
  readOnly: boolean;
  onNavigate?: () => void;
}) {
  const project = useProject();
  const collapsed = useCollapsedGroups();
  const renaming = useRenamingRow();
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const sensors = useSensors(
    // Mouse only: on touch screens a drag would fight scrolling, and the context menu can move items.
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    // Space picks an item up (Enter still opens it).
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates, keyboardCodes: { start: ['Space'], cancel: ['Escape'], end: ['Space', 'Enter'] } }),
  );

  const rows = useMemo(() => buildRows(tree, collapsed), [tree, collapsed]);
  // The placeholder inside an empty group is somewhere to drop, not something to sort.
  const ids = rows.filter((r) => r.kind !== 'empty').map((r) => r.key);
  const lifted = rows.find((r) => r.key === dragging);
  // Where the drop would land, so the group lights up before the mouse is let go.
  const landing = dragging && over ? resolveDrop(tree, rows, dragging, over) : null;
  const dropGroup = landing?.kind === 'collection' ? landing.groupId : null;

  const groups = useMemo(() => tree.flatMap((n) => (n.kind === 'group' ? [n.group] : [])), [tree]);

  // A row that went away — deleted, or another project's — must not leave the list mid-rename.
  useEffect(() => {
    if (renaming && !rows.some((r) => r.key === renaming)) stopRenaming();
  }, [renaming, rows]);

  const onDragEnd = ({ active, over: target }: DragEndEvent) => {
    const drop = target && resolveDrop(tree, rows, String(active.id), String(target.id));
    setDragging(null);
    setOver(null);
    if (drop) applyDrop(project, drop);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={({ active }) => setDragging(String(active.id))}
      onDragOver={({ over: target }: DragOverEvent) => setOver(target ? String(target.id) : null)}
      onDragEnd={onDragEnd}
      onDragCancel={() => {
        setDragging(null);
        setOver(null);
      }}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {rows.map((row) =>
          row.kind === 'group' ? (
            <GroupNavItem
              key={row.key}
              row={row}
              counts={counts}
              collapsed={collapsed.has(row.group.id)}
              dropping={dropGroup === row.group.id}
              renaming={renaming === row.key}
              readOnly={readOnly}
            />
          ) : row.kind === 'empty' ? (
            <EmptyGroupRow key={row.key} row={row} readOnly={readOnly} />
          ) : (
            <CollectionNavItem
              key={row.key}
              row={row}
              groups={groups}
              to={`${base}/c/${row.collection.id}`}
              count={counts[row.collection.id] ?? 0}
              renaming={renaming === row.key}
              onNavigate={onNavigate}
              readOnly={readOnly}
            />
          ),
        )}
      </SortableContext>
      <DragOverlay modifiers={[verticalOnly]} dropAnimation={{ duration: 180, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }}>
        {lifted?.kind === 'collection' ? (
          <div className="nav-item nav-item--lifted">
            <span className="nav-item__icon" data-color={lifted.collection.color}>
              <Icon token={lifted.collection.icon} />
            </span>
            <span className="nav-item__label">{lifted.collection.name}</span>
            <span className="nav-item__count">{counts[lifted.collection.id] ?? 0}</span>
          </div>
        ) : lifted?.kind === 'group' ? (
          <div className="nav-item nav-item--lifted">
            <FolderIcon />
            <span className="nav-item__label">{lifted.group.name}</span>
            <span className="nav-item__count">{lifted.collections.length}</span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

const FolderIcon = ({ open }: { open?: boolean }) => <span className="nav-item__icon">{open ? <FolderOpen /> : <Folder />}</span>;

/** The name field a group or a collection gets while it is renamed, in place in the list. */
function NavRename({ value, icon, onCommit }: { value: string; icon: ReactNode; onCommit: (name: string) => void }) {
  return (
    <div className="nav-item nav-item--renaming">
      {icon}
      <input
        autoFocus
        aria-label="Name"
        defaultValue={value}
        className="nav-item__label"
        onFocus={(e) => e.currentTarget.select()}
        onBlur={(e) => {
          onCommit(e.currentTarget.value.trim());
          stopRenaming();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            e.currentTarget.value = value;
            e.currentTarget.blur();
          }
        }}
      />
    </div>
  );
}

/**
 * A group's header: it folds the group open and shut and carries the group's own actions. The
 * keyboard activator is left off the drag so Space still works the button; a group is reordered
 * with the mouse, or from its menu.
 */
function GroupNavItem({
  row,
  counts,
  collapsed,
  dropping,
  renaming,
  readOnly,
}: {
  row: Extract<Row, { kind: 'group' }>;
  counts: Record<string, number>;
  collapsed: boolean;
  dropping: boolean;
  renaming: boolean;
  readOnly: boolean;
}) {
  const project = useProject();
  const confirm = useConfirm();
  const g = row.group;
  // Mouse drag only: the header is a real button, so Space must fold the group rather than lift it.
  // That leaves out dnd-kit's keyboard activator and the instructions that go with it; a group is
  // reordered from its menu instead.
  const { listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.key, disabled: readOnly || renaming });
  const { onKeyDown: _lift, ...dragHandle } = listeners ?? {};

  if (renaming) return <NavRename value={g.name} icon={<FolderIcon open />} onCommit={(name) => project.renameCollectionGroup(g.id, name)} />;

  const records = row.collections.reduce((n, c) => n + (counts[c.id] ?? 0), 0);
  const header = (
    <div
      ref={setNodeRef}
      className="nav-group"
      data-dragging={isDragging || undefined}
      data-dropping={dropping || undefined}
      style={{ transform: CSS.Translate.toString(transform), transition }}
    >
      <button type="button" className="nav-group__toggle" aria-expanded={!collapsed} onClick={() => toggleGroup(g.id)} {...(readOnly ? {} : dragHandle)}>
        <FolderIcon open={!collapsed} />
        <span className="nav-item__label">{g.name}</span>
        <span className="nav-item__count">{records || null}</span>
        <ChevronDown className="nav-group__chevron" />
      </button>
      {!readOnly && (
        <IconButton label={`New collection in ${g.name}`} size="xs" className="nav-group__add" onClick={() => openNewCollection(g.id)}>
          <Plus />
        </IconButton>
      )}
    </div>
  );
  if (readOnly) return header;

  const items: MenuItem[] = [
    { label: 'New collection…', icon: <Plus />, onSelect: () => openNewCollection(g.id) },
    { label: 'Rename', icon: <Pencil />, onSelect: () => startRenaming(row.key) },
    { type: 'separator' },
    { label: 'Move up', icon: <ArrowUp />, disabled: row.index === 0, onSelect: () => project.moveCollectionGroup(g.id, row.index - 1) },
    { label: 'Move down', icon: <ArrowDown />, disabled: row.last, onSelect: () => project.moveCollectionGroup(g.id, row.index + 1) },
    { type: 'separator' },
    {
      label: 'Delete group',
      icon: <Trash2 />,
      danger: true,
      onSelect: async () => {
        // Nothing is lost either way, so this only needs confirming when it moves things about.
        const ok =
          !row.collections.length ||
          (await confirm({
            title: `Delete “${g.name}”?`,
            description: `Its ${row.collections.length} collection${row.collections.length === 1 ? '' : 's'} stay in the project, where the group was.`,
            confirmLabel: 'Delete group',
            danger: true,
          }));
        if (ok) project.deleteCollectionGroup(g.id);
      },
    },
  ];
  return <ContextMenu items={items}>{header}</ContextMenu>;
}

/** A group nobody has filled yet: the one row that says what a group is for. */
function EmptyGroupRow({ row, readOnly }: { row: Extract<Row, { kind: 'empty' }>; readOnly: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id: row.key });
  if (readOnly) return null;
  return (
    <div ref={setNodeRef} className="nav-empty nav-empty--group" data-over={isOver || undefined}>
      <button type="button" onClick={() => openNewCollection(row.groupId)}>
        Add a collection
      </button>
      <span> or drag one in</span>
    </div>
  );
}

function CollectionNavItem({
  row,
  groups,
  to,
  count,
  renaming,
  onNavigate,
  readOnly,
}: {
  row: Extract<Row, { kind: 'collection' }>;
  groups: CollectionGroup[];
  to: string;
  count: number;
  renaming: boolean;
  onNavigate?: () => void;
  readOnly: boolean;
}) {
  const project = useProject();
  const confirm = useConfirm();
  const [iconOpen, setIconOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLAnchorElement | null>(null);
  const c = row.collection;
  // Keep link semantics: the item is still a link to the collection, which can also be picked up.
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: c.id, disabled: readOnly || renaming, attributes: { role: 'link' } });

  if (renaming) {
    return (
      <NavRename
        value={c.name}
        icon={
          <span className="nav-item__icon" data-color={c.color}>
            <Icon token={c.icon} />
          </span>
        }
        onCommit={(name) => project.renameCollection(c.id, name)}
      />
    );
  }

  const content = (
    <>
      <span className="nav-item__icon" data-color={c.color}>
        <Icon token={c.icon} />
      </span>
      <span className="nav-item__label">{c.name}</span>
      <span className="nav-item__count">{count}</span>
    </>
  );
  if (readOnly) {
    return (
      <NavLink to={to} className="nav-item" data-nested={row.groupId ? '' : undefined} onClick={onNavigate}>
        {content}
      </NavLink>
    );
  }

  const link = (
    <NavLink
      ref={(node) => {
        setNodeRef(node);
        setAnchor(node);
      }}
      to={to}
      className="nav-item"
      data-nested={row.groupId ? '' : undefined}
      data-dragging={isDragging || undefined}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      onClick={onNavigate}
      onDoubleClick={() => startRenaming(row.key)}
      {...attributes}
      {...listeners}
    >
      {content}
    </NavLink>
  );

  const items: MenuItem[] = [
    { label: 'Rename', icon: <Pencil />, onSelect: () => startRenaming(row.key) },
    { label: 'Icon & color', icon: <Shapes />, onSelect: () => setIconOpen(true) },
    { type: 'separator' },
    { label: 'Move up', icon: <ArrowUp />, disabled: row.index === 0, onSelect: () => project.moveCollection(c.id, row.index - 1) },
    { label: 'Move down', icon: <ArrowDown />, disabled: row.last, onSelect: () => project.moveCollection(c.id, row.index + 1) },
    // Every group by name, so filing a collection (or taking it out) never needs a drag to land right.
    ...(groups.length ? [{ type: 'label', label: 'Group' } as MenuItem] : []),
    ...groups.map<MenuItem>((g) => ({
      key: g.id,
      label: g.name,
      icon: <Folder />,
      checked: row.groupId === g.id,
      onSelect: () => project.setCollectionGroup(c.id, row.groupId === g.id ? null : g.id),
    })),
    ...(row.groupId ? [{ label: 'No group', icon: <FolderOpen />, onSelect: () => project.setCollectionGroup(c.id, null) } as MenuItem] : []),
    { type: 'separator' },
    {
      label: 'Move to trash',
      icon: <Trash2 />,
      danger: true,
      onSelect: async () => {
        const ok = await confirm({
          title: `Move “${c.name}” to trash?`,
          description: `Its ${count} record${count === 1 ? '' : 's'} go with it. You can restore everything from Trash.`,
          confirmLabel: 'Move to trash',
          danger: true,
        });
        if (ok) project.trashCollection(c.id);
      },
    },
  ];

  return (
    <>
      <ContextMenu items={items}>{link}</ContextMenu>
      <IconPicker
        anchor={anchor}
        open={iconOpen}
        onOpenChange={setIconOpen}
        value={c.icon}
        color={c.color}
        onColorChange={(color: PaletteColor) => project.updateCollection(c.id, { color })}
        onChange={(icon) => icon && project.updateCollection(c.id, { icon })}
      />
    </>
  );
}
